// WebAgentEngine - Browser-Compatible Technical Analysis Engine
// Provides real-time market analysis for any Monad token

// Types and constants
// TYPES
// ============================================

export interface TokenConfig {
    address: `0x${string}`;
    symbol: string;
    name: string;
}

export interface PriceData {
    price: number;
    timestamp: number;
    volume?: number;
}

export interface TechnicalIndicators {
    rsi: number;
    macd: { value: number; signal: number; histogram: number };
    bollingerBands: { upper: number; middle: number; lower: number; percentB: number };
    sma: { sma20: number; sma50: number };
    momentum: number;
    volatility: number;
}

export interface TradingSignal {
    type: 'BUY' | 'SELL' | 'HOLD';
    confidence: number;
    reason: string;
    indicators: TechnicalIndicators;
    timestamp: number;
}

export interface PricePrediction {
    direction: 'UP' | 'DOWN' | 'SIDEWAYS';
    confidence: number;
    horizons: {
        minutes5: { price: number; change: number };
        minutes15: { price: number; change: number };
        minutes60: { price: number; change: number };
    };
    timestamp: number;
}

export interface MarketAnalysis {
    volatilityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME';
    trendDirection: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
    trendStrength: number;
    fearGreedIndex: number;
    fearGreedLabel: string;
    healthScore: number;
    supportLevel: number;
    resistanceLevel: number;
    whaleAlert: boolean;
    alerts: { level: string; message: string }[];
}

// ============================================
// TECHNICAL ANALYSIS FUNCTIONS
// ============================================

/**
 * Calculate Relative Strength Index (RSI)
 */
export function calculateRSI(prices: number[], period: number = 14): number {
    if (prices.length < period + 1) return 50;

    let gains = 0;
    let losses = 0;

    // prices[0] = newest, prices[n] = oldest
    // change = newer - older (prices[i-1] is newer than prices[i])
    for (let i = 1; i <= period; i++) {
        const change = prices[i - 1] - prices[i];
        if (change > 0) gains += change;
        else losses -= change;
    }

    const avgGain = gains / period;
    const avgLoss = losses / period;

    if (avgLoss === 0) return 100;
    if (avgGain === 0) return 0;
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
}

/**
 * Calculate Simple Moving Average
 */
export function calculateSMA(prices: number[], period: number): number {
    if (prices.length < period) return prices[prices.length - 1] || 0;
    const slice = prices.slice(0, period);
    return slice.reduce((a, b) => a + b, 0) / period;
}

/**
 * Calculate Exponential Moving Average
 */
export function calculateEMA(prices: number[], period: number): number {
    if (prices.length < period) return calculateSMA(prices, prices.length);

    const multiplier = 2 / (period + 1);
    let ema = calculateSMA(prices.slice(-period), period);

    for (let i = prices.length - period; i < prices.length; i++) {
        ema = (prices[i] - ema) * multiplier + ema;
    }

    return ema;
}

/**
 * Calculate MACD (Moving Average Convergence Divergence)
 */
export function calculateMACD(prices: number[]): { value: number; signal: number; histogram: number } {
    const ema12 = calculateEMA(prices, 12);
    const ema26 = calculateEMA(prices, 26);
    const macdLine = ema12 - ema26;

    // Signal line: 9-period EMA of MACD values (approximated from available data)
    const macdHistory: number[] = [];
    for (let i = 0; i < Math.min(9, prices.length - 26); i++) {
        const slice = prices.slice(i);
        const e12 = calculateEMA(slice, 12);
        const e26 = calculateEMA(slice, 26);
        macdHistory.push(e12 - e26);
    }
    const signalLine = macdHistory.length > 0
        ? macdHistory.reduce((a, b) => a + b, 0) / macdHistory.length
        : macdLine;

    return {
        value: macdLine,
        signal: signalLine,
        histogram: macdLine - signalLine
    };
}

/**
 * Calculate Bollinger Bands
 */
export function calculateBollingerBands(prices: number[], period: number = 20): {
    upper: number;
    middle: number;
    lower: number;
    percentB: number;
} {
    const sma = calculateSMA(prices, period);
    const slice = prices.slice(0, Math.min(period, prices.length));

    // Calculate standard deviation
    const squaredDiffs = slice.map(p => Math.pow(p - sma, 2));
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / slice.length;
    const stdDev = Math.sqrt(variance);

    const upper = sma + (stdDev * 2);
    const lower = sma - (stdDev * 2);
    const currentPrice = prices[0];

    // Percent B: where price is within bands (0 = lower, 1 = upper)
    const percentB = (currentPrice - lower) / (upper - lower);

    return { upper, middle: sma, lower, percentB };
}

/**
 * Calculate momentum
 */
export function calculateMomentum(prices: number[], period: number = 10): number {
    if (prices.length < period) return 0;
    return ((prices[0] - prices[period - 1]) / prices[period - 1]) * 100;
}

/**
 * Calculate volatility (coefficient of variation)
 */
export function calculateVolatility(prices: number[]): number {
    if (prices.length < 2) return 0;

    const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
    const squaredDiffs = prices.map(p => Math.pow(p - mean, 2));
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / prices.length;
    const stdDev = Math.sqrt(variance);

    return (stdDev / mean) * 100;
}

// ============================================
// WEB AGENT ENGINE CLASS
// ============================================

export class WebAgentEngine {
    private priceHistory: number[] = [];
    private maxHistory: number = 100;
    private currentToken: TokenConfig;

    constructor(token: TokenConfig) {
        this.currentToken = token;
    }

    /**
     * Add new price to history
     */
    addPrice(price: number): void {
        this.priceHistory.unshift(price);
        if (this.priceHistory.length > this.maxHistory) {
            this.priceHistory.pop();
        }
    }

    /**
     * Set price history (for initialization)
     */
    setPriceHistory(prices: number[]): void {
        this.priceHistory = prices.slice(0, this.maxHistory);
    }

    /**
     * Get all technical indicators
     */
    getIndicators(): TechnicalIndicators {
        const prices = this.priceHistory;

        return {
            rsi: calculateRSI(prices),
            macd: calculateMACD(prices),
            bollingerBands: calculateBollingerBands(prices),
            sma: {
                sma20: calculateSMA(prices, 20),
                sma50: calculateSMA(prices, 50)
            },
            momentum: calculateMomentum(prices),
            volatility: calculateVolatility(prices)
        };
    }

    /**
     * Generate trading signal based on indicators
     */
    generateSignal(): TradingSignal {
        const indicators = this.getIndicators();

        let buyScore = 0;
        let sellScore = 0;
        const reasons: string[] = [];

        // RSI analysis
        if (indicators.rsi < 30) {
            buyScore += 25;
            reasons.push('RSI oversold');
        } else if (indicators.rsi > 70) {
            sellScore += 25;
            reasons.push('RSI overbought');
        }

        // MACD analysis
        if (indicators.macd.histogram > 0) {
            buyScore += 20;
            reasons.push('MACD bullish');
        } else if (indicators.macd.histogram < 0) {
            sellScore += 20;
            reasons.push('MACD bearish');
        }

        // Bollinger Bands analysis
        if (indicators.bollingerBands.percentB < 0.2) {
            buyScore += 20;
            reasons.push('Near lower band');
        } else if (indicators.bollingerBands.percentB > 0.8) {
            sellScore += 20;
            reasons.push('Near upper band');
        }

        // SMA crossover
        if (indicators.sma.sma20 > indicators.sma.sma50) {
            buyScore += 15;
            reasons.push('SMA20 > SMA50');
        } else {
            sellScore += 15;
            reasons.push('SMA20 < SMA50');
        }

        // Momentum
        if (indicators.momentum > 2) {
            buyScore += 20;
            reasons.push('Strong momentum');
        } else if (indicators.momentum < -2) {
            sellScore += 20;
            reasons.push('Weak momentum');
        }

        // Determine signal
        let type: 'BUY' | 'SELL' | 'HOLD' = 'HOLD';
        let confidence = 50;

        if (buyScore > sellScore + 20) {
            type = 'BUY';
            confidence = Math.min(95, 50 + buyScore - sellScore);
        } else if (sellScore > buyScore + 20) {
            type = 'SELL';
            confidence = Math.min(95, 50 + sellScore - buyScore);
        } else {
            confidence = 50 - Math.abs(buyScore - sellScore);
        }

        return {
            type,
            confidence: Math.round(confidence),
            reason: reasons.slice(0, 3).join(' | '),
            indicators,
            timestamp: Date.now()
        };
    }

    /**
     * Generate price prediction
     */
    generatePrediction(): PricePrediction {
        const indicators = this.getIndicators();
        const currentPrice = this.priceHistory[0] || 0;

        // Deterministic prediction based on momentum, trend, and indicator confluence
        const trendFactor = indicators.momentum / 100;
        const volatilityFactor = indicators.volatility / 100;

        // RSI mean-reversion component
        const rsiComponent = (50 - indicators.rsi) / 500; // Pulls toward mean

        // Bollinger Band position component
        const bbComponent = (0.5 - indicators.bollingerBands.percentB) * 0.02;

        // MACD momentum component
        const macdComponent = indicators.macd.histogram > 0 ? 0.005 : -0.005;

        // Weighted prediction (no randomness)
        const baseFactor = trendFactor * 0.4 + rsiComponent * 0.3 + bbComponent * 0.2 + macdComponent * 0.1;
        const change5m = baseFactor * 0.5 * (1 + volatilityFactor);
        const change15m = baseFactor * 1.2 * (1 + volatilityFactor);
        const change60m = baseFactor * 2.5 * (1 + volatilityFactor);

        // Determine direction
        let direction: 'UP' | 'DOWN' | 'SIDEWAYS' = 'SIDEWAYS';
        let confidence = 50;

        if (trendFactor > 0.01) {
            direction = 'UP';
            confidence = Math.min(85, 60 + indicators.momentum * 2);
        } else if (trendFactor < -0.01) {
            direction = 'DOWN';
            confidence = Math.min(85, 60 + Math.abs(indicators.momentum) * 2);
        }

        return {
            direction,
            confidence: Math.round(confidence),
            horizons: {
                minutes5: {
                    price: currentPrice * (1 + change5m),
                    change: change5m * 100
                },
                minutes15: {
                    price: currentPrice * (1 + change15m),
                    change: change15m * 100
                },
                minutes60: {
                    price: currentPrice * (1 + change60m),
                    change: change60m * 100
                }
            },
            timestamp: Date.now()
        };
    }

    /**
     * Generate market analysis
     */
    generateMarketAnalysis(): MarketAnalysis {
        const indicators = this.getIndicators();
        const currentPrice = this.priceHistory[0] || 0;
        const alerts: { level: string; message: string }[] = [];

        // Volatility level
        let volatilityLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME' = 'LOW';
        if (indicators.volatility > 15) volatilityLevel = 'EXTREME';
        else if (indicators.volatility > 8) volatilityLevel = 'HIGH';
        else if (indicators.volatility > 4) volatilityLevel = 'MEDIUM';

        // Trend analysis
        let trendDirection: 'BULLISH' | 'BEARISH' | 'NEUTRAL' = 'NEUTRAL';
        let trendStrength = Math.abs(indicators.momentum);

        if (indicators.momentum > 2) {
            trendDirection = 'BULLISH';
        } else if (indicators.momentum < -2) {
            trendDirection = 'BEARISH';
        }

        // Fear & Greed Index (0-100)
        let fearGreedIndex = 50;

        // RSI contribution
        if (indicators.rsi > 70) fearGreedIndex += 20;
        else if (indicators.rsi < 30) fearGreedIndex -= 20;
        else fearGreedIndex += (indicators.rsi - 50) * 0.5;

        // Momentum contribution
        fearGreedIndex += indicators.momentum * 2;

        // Volatility penalty
        if (indicators.volatility > 10) fearGreedIndex -= 10;

        fearGreedIndex = Math.max(0, Math.min(100, fearGreedIndex));

        let fearGreedLabel = 'Neutral';
        if (fearGreedIndex >= 80) fearGreedLabel = 'Extreme Greed';
        else if (fearGreedIndex >= 60) fearGreedLabel = 'Greed';
        else if (fearGreedIndex >= 40) fearGreedLabel = 'Neutral';
        else if (fearGreedIndex >= 20) fearGreedLabel = 'Fear';
        else fearGreedLabel = 'Extreme Fear';

        // Health score
        let healthScore = 50;
        if (trendDirection === 'BULLISH') healthScore += 15;
        else if (trendDirection === 'BEARISH') healthScore -= 15;
        if (volatilityLevel === 'EXTREME') healthScore -= 20;
        else if (volatilityLevel === 'HIGH') healthScore -= 10;
        healthScore = Math.max(0, Math.min(100, healthScore));

        // Support/Resistance (simplified)
        const sortedPrices = [...this.priceHistory].sort((a, b) => a - b);
        const supportLevel = sortedPrices[Math.floor(sortedPrices.length * 0.1)] || currentPrice * 0.95;
        const resistanceLevel = sortedPrices[Math.floor(sortedPrices.length * 0.9)] || currentPrice * 1.05;

        // Whale detection
        let whaleAlert = false;
        if (this.priceHistory.length >= 2) {
            const priceChange = Math.abs((this.priceHistory[0] - this.priceHistory[1]) / this.priceHistory[1]);
            if (priceChange > 0.03) {
                whaleAlert = true;
                alerts.push({ level: 'WARNING', message: `🐋 Whale activity detected: ${(priceChange * 100).toFixed(1)}% price swing` });
            }
        }

        // Add alerts based on conditions
        if (volatilityLevel === 'EXTREME') {
            alerts.push({ level: 'CRITICAL', message: 'Extreme volatility - exercise caution' });
        }
        if (indicators.rsi < 25) {
            alerts.push({ level: 'INFO', message: 'RSI indicates heavily oversold conditions' });
        } else if (indicators.rsi > 75) {
            alerts.push({ level: 'WARNING', message: 'RSI indicates overbought conditions' });
        }

        return {
            volatilityLevel,
            trendDirection,
            trendStrength,
            fearGreedIndex: Math.round(fearGreedIndex),
            fearGreedLabel,
            healthScore: Math.round(healthScore),
            supportLevel,
            resistanceLevel,
            whaleAlert,
            alerts
        };
    }

    /**
     * Get current token
     */
    getToken(): TokenConfig {
        return this.currentToken;
    }

    /**
     * Get current price
     */
    getCurrentPrice(): number {
        return this.priceHistory[0] || 0;
    }

    /**
     * Get price history
     */
    getPriceHistory(): number[] {
        return [...this.priceHistory];
    }
}

// ============================================
// POPULAR TOKENS ON MONAD
// ============================================

export const POPULAR_MONAD_TOKENS: TokenConfig[] = [
    {
        address: '0x0862F464c8457266b66c58F1D7C1137B72647777' as `0x${string}`,
        symbol: 'DUCK',
        name: 'Duck on Monad'
    },
    {
        address: '0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A' as `0x${string}`,
        symbol: 'MON',
        name: 'Monad'
    },
    {
        address: '0x760AfE86e5de5fa0Ee542fc7B7B713e1c5425701' as `0x${string}`, // WMON
        symbol: 'WMON',
        name: 'Wrapped MON'
    }
];

// Default export
export default WebAgentEngine;
